import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppLogo from "@/components/AppLogo";
import { hasPerm } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  FolderKanban,
  ListChecks,
  Bug,
  BarChart3,
  Clock,
  Shield,
  Briefcase,
  LineChart,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
  { to: "/my-work", label: "My Work", icon: Briefcase, testid: "sidebar-nav-my-work" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, perm: "task.read", testid: "sidebar-nav-tasks" },
  { to: "/projects", label: "Projects", icon: FolderKanban, perm: "project.read", testid: "sidebar-nav-projects" },
  { to: "/teams", label: "Teams", icon: UsersRound, perm: "team.read", testid: "sidebar-nav-teams" },
  { to: "/bugs", label: "Bugs", icon: Bug, perm: "bug.read", testid: "sidebar-nav-bugs" },
  { to: "/timelogs", label: "Time Logs", icon: Clock, perm: "timelog.read", testid: "sidebar-nav-timelogs" },
  { to: "/work-reports", label: "Work Report", icon: FileText, testid: "sidebar-nav-work-reports" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "report.read", testid: "sidebar-nav-reports" },
  { to: "/analytics", label: "Analytics", icon: LineChart, perm: "analytics.read", testid: "sidebar-nav-analytics" },
  { to: "/users", label: "Users", icon: Users, perm: "user.read", testid: "sidebar-nav-users" },
  { to: "/roles", label: "Roles", icon: Shield, perm: "role.read", testid: "sidebar-nav-roles" },
];

export default function Sidebar({ collapsed, toggleCollapse, mobileOpen, setMobileOpen }) {
  const { user } = useAuth();

  return (
    <>
      {/* Mobile Sidebar overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm md:hidden animate-in fade-in duration-200"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed md:sticky top-0 left-0 z-50 md:z-auto h-screen bg-card border-r border-border transition-all duration-300 flex flex-col ${
          mobileOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0"
        } ${collapsed ? "md:w-16" : "md:w-64"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-border shrink-0">
          {collapsed && !mobileOpen ? (
            /* Collapsed view: logo with hover-to-toggle */
            <div className="relative group w-8 h-8 flex items-center justify-center shrink-0 mx-auto">
              <div className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0 flex items-center justify-center">
                <AppLogo className="w-8 h-8 rounded-md object-cover" />
              </div>
              <div className="absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleCollapse}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
                  data-testid="sidebar-toggle-btn-collapsed"
                  title="Expand Sidebar"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            /* Open view: logo + title, and close button */
            <>
              <div className="flex items-center gap-3 overflow-hidden">
                <AppLogo className="w-8 h-8 rounded-md object-cover shrink-0" />
                <div className="flex flex-col leading-tight animate-in fade-in duration-200">
                  <span className="font-heading font-bold text-base whitespace-nowrap">Infoz IT </span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">Solutions</span>
                </div>
              </div>
              {/* Desktop Close Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCollapse}
                className="h-8 w-8 text-muted-foreground hover:text-foreground hidden md:flex shrink-0 animate-in fade-in duration-200"
                data-testid="sidebar-toggle-btn-open"
                title="Collapse Sidebar"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {/* Mobile Close Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground md:hidden shrink-0"
                data-testid="sidebar-mobile-close-btn"
                title="Close Menu"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
        
        <TooltipProvider delayDuration={100}>
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {NAV.filter((n) => !n.perm || hasPerm(user, n.perm)).map((n) => {
              const Icon = n.icon;
              const showLabel = !collapsed || mobileOpen;
              
              const link = (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center rounded-md text-sm font-medium transition-colors ${
                      !showLabel ? "justify-center p-2" : "gap-3 px-3 py-2"
                    } ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {showLabel && <span className="animate-in fade-in duration-200 whitespace-nowrap">{n.label}</span>}
                </NavLink>
              );

              if (!showLabel) {
                return (
                  <Tooltip key={n.to}>
                    <TooltipTrigger asChild>
                      {link}
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {n.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return link;
            })}
          </nav>
        </TooltipProvider>
        <div className={`p-4 border-t border-border flex flex-col ${collapsed && !mobileOpen ? "items-center" : ""}`}>
          {collapsed && !mobileOpen ? (
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs animate-in zoom-in-50 duration-200" title={`${user?.name} (${user?.email})`}>
              {(user?.name || "U").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          ) : (
            <div className="animate-in fade-in duration-200 whitespace-nowrap overflow-hidden w-full">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 whitespace-nowrap">Signed in</div>
              <div className="text-sm font-medium truncate w-full whitespace-nowrap">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate w-full whitespace-nowrap">{user?.email}</div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
